/** Enum labels and tones shared by pages. Copy is terse and factual. */
import { api, ROLE_LABEL, type Capability, type Role } from '@agency/shared';
import type { ChipTone, ComponentState, StatusTone } from '@agency/ui';

export function roleLabel(role: Role): string {
  return ROLE_LABEL[role];
}

export const ROLE_SHORT: Record<Role, string> = {
  agency_administrator: 'Admin',
  forward_deployed_operator: 'Operator',
  template_editor: 'Editor',
  template_publisher: 'Publisher',
  audit_reviewer: 'Auditor',
};

export const USER_STATUS_TONE: Record<api.UserStatus, ChipTone> = { active: 'ok', invited: 'info', suspended: 'warn', removed: 'mute' };
export const INVITATION_TONE: Record<api.InvitationStatus, ChipTone> = { pending: 'info', accepted: 'ok', expired: 'mute', cancelled: 'mute' };
export const DEVICE_STATUS_TONE: Record<api.DeviceStatus, ChipTone> = { active: 'ok', revoked: 'mute', update_required: 'warn' };
export const DEVICE_STATUS_LABEL: Record<api.DeviceStatus, string> = { active: 'Active', revoked: 'Revoked', update_required: 'Needs update' };
export const AI_KEY_STATUS_TONE: Record<api.AiKeyStatus, ChipTone> = { active: 'ok', disabled: 'warn', revoke_pending: 'warn', revoked: 'mute', superseded: 'mute' };
export const AI_KEY_STATUS_LABEL: Record<api.AiKeyStatus, string> = { active: 'Active', disabled: 'Disabled', revoke_pending: 'Revoke pending', revoked: 'Revoked', superseded: 'Superseded' };
export const REVOCATION_LABEL: Record<api.AiKey['providerRevocationState'], string> = { not_requested: 'Not requested', pending: 'Pending', confirmed: 'Confirmed', failed: 'Failed' };
export const REVOCATION_TONE: Record<api.AiKey['providerRevocationState'], StatusTone> = { not_requested: 'dim', pending: 'warn', confirmed: 'ok', failed: 'brand' };
export const TEMPLATE_STATE_TONE: Record<api.TemplateVersionState, ChipTone> = { draft: 'mute', validated: 'info', previewed: 'info', reviewed: 'warn', published: 'ok', active: 'ok', rolled_back: 'mute' };
export const TEMPLATE_STATE_LABEL: Record<api.TemplateVersionState, string> = { draft: 'Draft', validated: 'Validated', previewed: 'Previewed', reviewed: 'Reviewed', published: 'Published', active: 'Active', rolled_back: 'Rolled back' };
export const TEMPLATE_TYPE_LABEL: Record<api.TemplateType, string> = {
  agenda_template: 'Agenda templates',
  email_template: 'Email templates',
  remediation_ask: 'Remediation asks',
  classification_rule: 'Classification rules',
  matching_rule: 'Matching rules',
  report_columns: 'Report columns',
  help_content: 'Help content',
  feature_flags: 'Feature flags',
  model_config: 'Model config',
  rate_limit_config: 'Rate limits',
};
export const TEMPLATE_TYPE_SINGULAR: Record<api.TemplateType, string> = {
  agenda_template: 'Agenda template',
  email_template: 'Email template',
  remediation_ask: 'Remediation ask',
  classification_rule: 'Classification rule',
  matching_rule: 'Matching rule',
  report_columns: 'Report columns',
  help_content: 'Help content',
  feature_flags: 'Feature flags',
  model_config: 'Model config',
  rate_limit_config: 'Rate limit config',
};
export const ROLLOUT_LABEL: Record<api.RolloutStage, string> = { cohort_3: 'Cohort 3', cohort_10: 'Cohort 10', all: 'All users', halted: 'Halted' };
export const ROLLOUT_TONE: Record<api.RolloutStage, ChipTone> = { cohort_3: 'info', cohort_10: 'info', all: 'ok', halted: 'warn' };
export const OUTCOME_TONE: Record<api.AuditOutcome, ChipTone> = { success: 'ok', denied: 'brand', failed: 'warn', unknown: 'mute' };
export const TRUST_LABEL: Record<api.AuditTrustLevel, string> = { device_reported: 'Device reported', server_observed: 'Server observed', provider_reconciled: 'Provider reconciled' };
export const TRUST_TONE: Record<api.AuditTrustLevel, ChipTone> = { device_reported: 'mute', server_observed: 'info', provider_reconciled: 'ok' };
export const USAGE_OUTCOME_LABEL: Record<api.UsageRecord['outcome'], string> = { success: 'Success', failed: 'Failed', cancelled: 'Cancelled', unknown: 'Unknown' };
export const VERIFICATION_LABEL: Record<'estimated' | 'reconciled' | 'unknown', string> = { estimated: 'Estimated', reconciled: 'Reconciled', unknown: 'Unknown' };
export const VERIFICATION_TONE: Record<'estimated' | 'reconciled' | 'unknown', ChipTone> = { estimated: 'warn', reconciled: 'ok', unknown: 'mute' };
export const HEALTH_TONE: Record<'healthy' | 'degraded' | 'unauthorized' | 'disconnected' | 'unknown', StatusTone> = { healthy: 'ok', degraded: 'warn', unauthorized: 'brand', disconnected: 'dim', unknown: 'dim' };
export const HEALTH_STATE: Record<'healthy' | 'degraded' | 'unauthorized' | 'disconnected' | 'unknown', ComponentState> = { healthy: 'complete', degraded: 'partial', unauthorized: 'unauthorized', disconnected: 'disconnected', unknown: 'empty' };
export const OUTBOX_STATE_TONE: Record<'pending' | 'in_progress' | 'succeeded' | 'failed' | 'dead', ChipTone> = { pending: 'info', in_progress: 'info', succeeded: 'ok', failed: 'warn', dead: 'brand' };
export const MAIL_STATE_TONE: Record<'queued' | 'sent' | 'delivered' | 'bounced' | 'failed', ChipTone> = { queued: 'info', sent: 'ok', delivered: 'ok', bounced: 'brand', failed: 'warn' };
export const COMPONENT_TONE: Record<'ok' | 'degraded' | 'down' | 'unknown', StatusTone> = { ok: 'ok', degraded: 'warn', down: 'brand', unknown: 'dim' };
export const PROVIDER_LABEL: Record<api.Account['provider'], string> = { vanta: 'VANTA', drata: 'DRATA', template_only: 'TEMPLATE' };

export function capabilityLabel(cap: Capability): string {
  return cap;
}

/** `sk-...4f2a` style masks stay as given; never alter case. */
export function humanize(value: string): string {
  return value.replace(/_/g, ' ');
}

export function pluralize(n: number, singular: string, plural = `${singular}s`): string {
  return `${n.toLocaleString('en-US')} ${n === 1 ? singular : plural}`;
}
